import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import PeoplePicker from '../components/PeoplePicker';
import { downloadCsv, downloadXlsx } from '../lib/exportTable';
import { fmtDateTime } from '../lib/when';
import { settingOfKey } from './settings/registry';
import './AdminAuditLog.css';

/**
 * ADVANCED -> AUDIT LOG. Administrators only, as the server is.
 *
 * Every log Jarvis keeps, as one list, newest first (GET /admin/audit-log,
 * lib/audit-sources.js on the server): the ticket trail, time, devices and
 * their notes, settings and feature switches, docs and who opened them,
 * chat edits and deletions, conversation review, people, licences, to-dos,
 * links, roll-calls, automation rules, email in and out - and since 121
 * sign-ins, View as sessions and recordings opened by somebody not on the
 * call.
 *
 * The filters - dates, people, the log, the action, what it is about, and
 * words - live in the address, so a filtered log can be bookmarked or sent.
 * The list pages by cursor ("Show older"), so nothing repeats or goes
 * missing when new entries arrive while reading. Export writes exactly the
 * rows the filters match, as CSV or Excel, through lib/exportTable.js.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const FILTERS = ['from', 'to', 'actor', 'source', 'action', 'subject_type', 'subject_id', 'subject_label', 'q'];
const PAGE = 50;

/** What a row can be about, for the filter and the row's "only this". */
const SUBJECTS = [
  ['ticket', 'A ticket'], ['asset', 'A device'], ['licence', 'A licence'], ['doc', 'A doc'],
  ['user', 'A member of staff'], ['contractor', 'A contractor'], ['setting', 'A setting'], ['feature', 'A feature switch'],
  ['recording', 'A recording'], ['rule', 'An automation rule'], ['chat_group', 'A chat group'],
  ['chat_direct', 'A direct conversation'], ['todo', 'A to-do'], ['todo_list', 'A to-do list'],
  ['roll_call', 'A roll-call'], ['email', 'An email'], ['report', 'The monthly report']
];

/** Colour by log, so a page of many logs can be read at a glance. */
const TONE = {
  tickets: 'badge-comment', hours: 'badge-status', assets: 'badge-priority', asset_notes: 'badge-priority',
  features: 'badge-feature', settings: 'badge-feature', docs: 'badge-docs', doc_views: 'badge-docs',
  chat: 'badge-chat', chat_review: 'badge-chat', people: 'badge-people', licences: 'badge-licence',
  todos: 'badge-update', links: 'badge-update', roll_call: 'badge-people', workflows: 'badge-status',
  mail: 'badge-docs', sign_ins: 'badge-signin', view_as: 'badge-viewas', recordings: 'badge-chat'
};

const errorOf = (e, fallback) => e?.response?.data?.error || fallback;

/** A settings row names its key; the settings registry knows its label and page. */
function withSetting(row) {
  if (row.source !== 'settings' || !row.key) return row;
  const hit = settingOfKey(row.key);
  if (!hit) return row;
  return {
    ...row,
    subject_label: hit.setting.label,
    link: hit.path,
    description: row.description.replace(row.key, `“${hit.setting.label}”`)
  };
}

export default function AdminAuditLog() {
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null);
  const [next, setNext] = useState(null);
  const [reason, setReason] = useState(null);
  const [skipped, setSkipped] = useState([]);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [options, setOptions] = useState(null);
  const [exporting, setExporting] = useState('');
  const [note, setNote] = useState('');
  const [query, setQuery] = useState(params.get('q') || '');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filters = useMemo(() => Object.fromEntries(FILTERS.map((k) => [k, params.get(k) || ''])
    .filter(([k, v]) => v !== '' && k !== 'subject_label')), [params]);
  const key = JSON.stringify(filters);

  // Only the latest request's answer is shown: an older, slower one (the
  // search a moment ago) arriving last would otherwise put its rows back.
  const latest = useRef(0);
  const load = useCallback(() => {
    const n = ++latest.current;
    setLoading(true);
    return axios.get(`${API_BASE}/admin/audit-log`, { params: { ...JSON.parse(key), limit: PAGE } })
      .then((r) => {
        if (n !== latest.current) return;
        setRows((r.data.data || []).map(withSetting));
        setTotal(r.data.total ?? null);
        setNext(r.data.next_cursor || null);
        // The server says why a list is empty, rather than leaving "nothing
        // happened" and "the query could not run" looking identical.
        setReason(r.data.reason || null);
        setSkipped(r.data.skipped || []);
      })
      .catch((e) => {
        if (n !== latest.current) return;
        setRows([]); setTotal(0); setNext(null); setSkipped([]);
        setReason(errorOf(e, 'The server did not answer. Try again, or check the API is up.'));
      })
      .finally(() => { if (n === latest.current) setLoading(false); });
  }, [key]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API_BASE}/admin/audit-log/options`)
      .then((r) => setOptions(r.data))
      .catch(() => setOptions({ sources: [], people: [] }));
  }, []);

  /** The next, older page, onto the end of this one. */
  const showOlder = () => {
    if (!next) return;
    const n = latest.current;
    setMore(true);
    axios.get(`${API_BASE}/admin/audit-log`, { params: { ...filters, limit: PAGE, cursor: next, count: 0 } })
      .then((r) => {
        if (n !== latest.current) return;
        setRows((was) => [...was, ...(r.data.data || []).map(withSetting)]);
        setNext(r.data.next_cursor || null);
      })
      .catch((e) => { if (n === latest.current) setNote(errorOf(e, 'Could not load the older entries.')); })
      .finally(() => setMore(false));
  };

  // The address as it is this moment. The router's own copy (params) is
  // updated in a transition, so a click just made may not be in it yet.
  const liveParams = () => new URLSearchParams(window.location.search);

  // The search box writes the address a moment after typing stops.
  useEffect(() => {
    const t = setTimeout(() => {
      const now = liveParams();
      if ((now.get('q') || '') === query.trim()) return;
      if (query.trim()) now.set('q', query.trim()); else now.delete('q');
      setParams(now, { replace: true });
    }, 250);
    return () => clearTimeout(t);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  const setParam = (changes) => {
    const nextParams = liveParams();
    for (const [k, v] of Object.entries(changes)) {
      if (v) nextParams.set(k, v); else nextParams.delete(k);
    }
    setParams(nextParams);
  };
  const onlyThis = (row) => setParam({ subject_type: row.subject_type, subject_id: row.subject_id, subject_label: row.subject_label || '' });
  const byPerson = (row) => row.actor_key && setParam({ actor: row.actor_key });
  const anyFilter = FILTERS.some((k) => params.get(k));
  const clear = () => { setQuery(''); setParams(new URLSearchParams()); };

  const sources = (options?.sources || []).filter((s) => s.ready);
  const chosenSource = params.get('source') || '';
  const actionGroups = (chosenSource ? sources.filter((s) => s.key === chosenSource) : sources);
  const people = (options?.people || []).map((p) => ({
    id: p.id, name: p.name, company: p.kind === 'contractor' ? (p.company || 'Contractor') : null
  }));
  const actors = (params.get('actor') || '').split(',').filter(Boolean);
  // A select shows as much of its choice as fits; the whole of it is its title.
  const sourceTitle = sources.find((s) => s.key === chosenSource)?.label || 'Every log';
  const actionTitle = sources.flatMap((s) => s.actions).find((a) => a.value === params.get('action'))?.label || 'Every action';
  const aboutTitle = SUBJECTS.find(([v]) => v === params.get('subject_type'))?.[1] || 'About anything';
  // On a phone the filters fold behind one button that says how many are on;
  // the search stays out. Words are not counted - they are in plain sight.
  const activeCount = ['from', 'to', 'actor', 'source', 'action', 'subject_type'].filter((k) => params.get(k)).length;

  const exportAs = async (kind) => {
    setExporting(kind);
    setNote('');
    try {
      const r = (await axios.get(`${API_BASE}/admin/audit-log/export`, { params: filters, timeout: 0 })).data;
      // lib/exportTable adds the day to the name itself.
      const name = 'audit-log';
      const sheet = r.rows.map((x) => ({ ...x }));
      if (kind === 'csv') downloadCsv(r.columns, sheet, name); else downloadXlsx(r.columns, sheet, name, 'Audit log');
      if (r.capped) setNote(`Only the newest ${r.max.toLocaleString('en-GB')} entries were exported. Narrow the dates for the rest.`);
    } catch (e) {
      setNote(errorOf(e, 'The export did not work.'));
    }
    setExporting('');
  };

  const shownCount = total === null ? `${rows.length}` : `${rows.length} of ${total}`;

  return (
    <div className="audit-container" data-testid="audit-log">
      <header className="audit-header">
        <div>
          <h1>Audit log</h1>
          <p className="audit-lead">Who did what, and when, across every log Jarvis keeps - newest first.</p>
        </div>
        <div className="audit-head-actions">
          <button type="button" className="refresh-btn" onClick={load}>↻ Refresh</button>
          <button type="button" className="audit-export" disabled={Boolean(exporting)} onClick={() => exportAs('csv')}>
            {exporting === 'csv' ? 'Exporting…' : 'Export CSV'}
          </button>
          <button type="button" className="audit-export" disabled={Boolean(exporting)} onClick={() => exportAs('xlsx')}>
            {exporting === 'xlsx' ? 'Exporting…' : 'Export Excel'}
          </button>
        </div>
      </header>

      <div className="audit-controls" data-testid="audit-filters">
        <div className="audit-controls-top">
          <input type="search" className="audit-search" placeholder="Search what happened, who, or what it was about"
            aria-label="Search the audit log" value={query} onChange={(e) => setQuery(e.target.value)} />
          <button type="button" className="audit-filters-toggle" data-testid="audit-filters-toggle"
            aria-expanded={filtersOpen} aria-controls="audit-filter-row" onClick={() => setFiltersOpen((v) => !v)}>
            Filters{activeCount > 0 ? ` (${activeCount})` : ''}
          </button>
          {anyFilter && <button type="button" className="audit-clear" onClick={clear}>Clear filters</button>}
        </div>
        <div id="audit-filter-row" className={filtersOpen ? 'audit-filter-row is-open' : 'audit-filter-row'}>
          <label className="audit-field audit-date">
            <span>From</span>
            <input type="date" value={params.get('from') || ''} aria-label="From" onChange={(e) => setParam({ from: e.target.value })} />
          </label>
          <label className="audit-field audit-date">
            <span>To</span>
            <input type="date" value={params.get('to') || ''} aria-label="To" onChange={(e) => setParam({ to: e.target.value })} />
          </label>
          <div className="audit-people">
            <PeoplePicker label="Who" allLabel="Anybody" options={people} value={actors}
              onChange={(ids) => setParam({ actor: ids.join(',') })} idBase="audit-who" />
          </div>
          <select className="audit-select audit-select-log" value={chosenSource} aria-label="Log" title={sourceTitle}
            onChange={(e) => setParam({ source: e.target.value, action: '' })}>
            <option value="">Every log</option>
            {sources.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select className="audit-select audit-select-action" value={params.get('action') || ''} aria-label="Action" title={actionTitle}
            onChange={(e) => setParam({ action: e.target.value })}>
            <option value="">Every action</option>
            {actionGroups.map((s) => (
              <optgroup key={s.key} label={s.label}>
                {s.actions.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </optgroup>
            ))}
          </select>
          <select className="audit-select audit-select-about" value={params.get('subject_type') || ''} aria-label="About" title={aboutTitle}
            onChange={(e) => setParam({ subject_type: e.target.value, subject_id: '', subject_label: '' })}>
            <option value="">About anything</option>
            {SUBJECTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>

      {params.get('subject_id') && (
        <p className="audit-chip" data-testid="audit-subject-chip">
          Only entries about{' '}<strong>{params.get('subject_label') || params.get('subject_id')}</strong>
          <button type="button" aria-label="Show entries about anything"
            onClick={() => setParam({ subject_id: '', subject_label: '', subject_type: '' })}>✕</button>
        </p>
      )}

      {note && <p className="audit-note" role="status">{note}</p>}
      {skipped.length > 0 && (
        <p className="audit-note" data-testid="audit-skipped">
          Not on this server yet: {skipped.map((s) => s.label).join(', ')} - their migration has not been run.
        </p>
      )}

      <div className="pagination-info" aria-live="polite">
        {loading ? 'Loading…' : total === 0 ? 'No entries' : `Showing ${shownCount} entries`}
      </div>

      {!loading && rows.length === 0 ? (
        <div className="empty-state">
          {reason ? (
            <>
              <strong>The audit log could not be read</strong>
              <p style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>{reason}</p>
            </>
          ) : anyFilter ? (
            <>
              <strong>Nothing matches these filters</strong>
              <p style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
                <button type="button" className="audit-clear" onClick={clear}>Clear filters</button> to see everything recorded.
              </p>
            </>
          ) : (
            <>
              <strong>Nothing has been recorded yet</strong>
              <p style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
                Entries appear when somebody changes something - a ticket, a device, a setting -
                signs in, or opens something an audit has to know about.
              </p>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="audit-table" role="table" aria-label="Audit log">
            <div className="table-header" role="row">
              <div className="col-date" role="columnheader">When</div>
              <div className="col-actor" role="columnheader">Who</div>
              <div className="col-action" role="columnheader">Log</div>
              <div className="col-ticket" role="columnheader">About</div>
              <div className="col-change" role="columnheader">What happened</div>
            </div>

            {rows.map((log) => (
              <div key={log.id} className="table-row" role="row" data-source={log.source} data-action={log.action}>
                <div className="col-date" role="cell">{fmtDateTime(log.created_at)}</div>
                <div className="col-actor" role="cell">
                  {log.actor_key ? (
                    <button type="button" className="actor-name audit-linkish" title="Only this person’s entries"
                      onClick={() => byPerson(log)}>{log.actor_name}</button>
                  ) : <div className="actor-name">{log.actor_name}</div>}
                  {log.actor_email && <div className="actor-email">{log.actor_email}</div>}
                  {log.on_behalf_by && (
                    <div className="actor-on-behalf" title={log.on_behalf_by_email || ''}>
                      via {log.on_behalf_by}
                    </div>
                  )}
                </div>
                <div className="col-action" role="cell">
                  <span className={`action-badge ${TONE[log.source] || 'badge-default'}`} title={log.action_label}>
                    {log.source_label}
                  </span>
                </div>
                <div className="col-ticket" role="cell">
                  <div className="ticket-info">
                    {log.area && <div className="ticket-number">{log.area}</div>}
                    {log.subject_label && (log.link
                      ? <Link className="ticket-title" to={log.link}>{log.subject_label}</Link>
                      : <div className="ticket-title">{log.subject_label}</div>)}
                    {log.subject_id && log.subject_type && (
                      <button type="button" className="audit-only" onClick={() => onlyThis(log)}
                        aria-label={`Only entries about ${log.subject_label || 'this'}`}>Only this</button>
                    )}
                  </div>
                </div>
                <div className="col-change" role="cell">
                  <div className={log.action === 'commented' ? 'change-comment' : 'change-said'}>{log.description}</div>
                  {log.browser && <div className="audit-browser" title={log.browser}>{log.browser}</div>}
                </div>
              </div>
            ))}
          </div>

          {next && (
            <div className="pagination">
              <button type="button" className="btn btn-next" onClick={showOlder} disabled={more}>
                {more ? 'Loading…' : 'Show older'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
